import {BOOKING_CONSTRAINTS} from '@/constants/booking';
import {formatDHM} from '@/utils/dateUtils';

const FLIGHT_API_BASE = 'https://aerodatabox.p.rapidapi.com/flights/number';
const FLIGHT_API_KEY = import.meta.env.VITE_FLIGHT_API_KEY;

export const FLIGHT_API_HEADERS = {
  Accept: 'application/json',
  'x-rapidapi-host': 'aerodatabox.p.rapidapi.com',
  'x-rapidapi-key': FLIGHT_API_KEY,
};

export const flightService = {
  // dateRole tells the API whether `date` is the flight's Departure or Arrival
  // local date. From-airport pickups select the ARRIVAL date, so pass 'Arrival'
  // there — otherwise an overnight flight departing on day N (queried as
  // Departure) arrives on N+1 and the recommended pickup lands a day late.
  async getFlightDetails(flightNumber, date, dateRole = 'Departure') {
    const trimmed = flightNumber.trim();
    if (!trimmed) {
      return {success: false, data: [], error: 'Flight number is required'};
    }

    let url = `${FLIGHT_API_BASE}/${trimmed}`;
    if (date) url += `/${date}?dateLocalRole=${dateRole}`;

    try {
      const response = await fetch(url, {method: 'GET', headers: FLIGHT_API_HEADERS});
      if (!response.ok) {
        // Provider-side failure (blocked key, quota/rate limit, 5xx). `unavailable`
        // lets callers tell "we couldn't reach the provider" apart from "the provider
        // answered, but there's no such flight" — the former must NOT invalidate a
        // flight that's already saved on the booking.
        return {success: false, data: [], error: 'Failed to fetch flight details', unavailable: true};
      }
      const text = await response.text();
      if (!text.trim()) {
        return {success: false, data: [], error: 'No flight found with this number'};
      }
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        return {success: false, data: [], error: 'No flight found with this number'};
      }
      if (Array.isArray(data) && data.length > 0) {
        return {success: true, data, error: null};
      }
      return {success: false, data: [], error: 'No flight found with this number'};
    } catch {
      // Network error / request never completed — also a provider-reachability failure.
      return {success: false, data: [], error: 'Failed to fetch flight details', unavailable: true};
    }
  },

  // Domestic vs International, derived from the flight's OWN airports. The
  // flight-number popup has no International/Domestic toggle — only the typed-time path
  // asks for it — so a looked-up flight must not be labelled from the stored toggle
  // (it defaults to "International" and made YYZ→YUL read as an international flight).
  // Returns null when either country is missing, so callers can fall back.
  getFlightType(flight) {
    const from = flight?.departure?.airport?.countryCode;
    const to = flight?.arrival?.airport?.countryCode;
    if (!from || !to) return null;
    return from === to ? 'Domestic' : 'International';
  },

  parseUtcTime(utcTime) {
    let normalized = utcTime.trim().replace(' ', 'T');
    if (!/[Zz]$/.test(normalized) && !/[+-]\d{2}:\d{2}$/.test(normalized)) {
      normalized += 'Z';
    }
    return new Date(normalized).getTime();
  },

  isFlightUpcoming(utcTime) {
    if (!utcTime) return {ok: false, reason: 'passed'};
    const flightTime = this.parseUtcTime(utcTime);
    const now = Date.now();
    const minLeadMs = BOOKING_CONSTRAINTS.MINIMUM_BOOKING_LEAD_TIME_MINUTES * 60 * 1000;
    if (flightTime <= now) return {ok: false, reason: 'passed'};
    if (flightTime <= now + minLeadMs) return {ok: false, reason: 'too_soon'};
    return {ok: true, reason: null};
  },

  formatMinLeadTime() {
    return formatDHM(BOOKING_CONSTRAINTS.MINIMUM_BOOKING_LEAD_TIME_MINUTES);
  },

  parseLocalTime(dateString) {
    const stripped = dateString.replace(' ', 'T').replace(/[Zz]$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
    return new Date(stripped);
  },

  formatFlightTime(dateString) {
    return this.parseLocalTime(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  },

  formatFlightDate(dateString) {
    return this.parseLocalTime(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  },
};

export default flightService;
